
import BRadioGroup from 'JS/components/blain-form/BRadioGroup.vue';
import TimeSlotCalendarDatePicker from './TimeSlotCalendarDatePicker.vue';
import TimeSlotCalendarDatePickerDay from './TimeSlotCalendarDatePickerDay.vue';
import TimeSlotCalendarOption from './TimeSlotCalendarOption.vue';
import { addDays, differenceInCalendarDays, eachDayOfInterval, format, isSameDay, startOfDay, subDays } from 'date-fns/fp';
import { computed, defineComponent, nextTick, ref, watch } from 'vue';
import { DateRange } from 'JS/types/Generic';
import { PropType } from 'vue';
import { TimeSlotLookup } from 'JS/automotive/auto-service-scheduler/utils/calendar';

export default defineComponent({
	components: {
		BRadioGroup,
		TimeSlotCalendarDatePicker,
		TimeSlotCalendarDatePickerDay,
		TimeSlotCalendarOption,
	},
	model: {
		prop: 'modelValue',
		event: 'update:modelValue',
	},
	props: {
		modelValue: {
			type: Object as PropType<DateRange>,
		},
		availableTimeSlots: {
			type: Object as PropType<TimeSlotLookup>,
			required: true,
		}
	},
	setup (props, { emit }) {
		const startDate = computed(() => subDays(2, props.availableTimeSlots.values[0]?.start ?? new Date));
		const endDate = computed(() => {
			let endDate = addDays(2, props.availableTimeSlots.values[props.availableTimeSlots.values.length - 1]?.start ?? addDays(30, new Date));

			// ensure we have a reasonable number of days to display so as not to bork the UI
			const numberOfDays = differenceInCalendarDays(startDate.value, endDate);
			if (numberOfDays < 10) {
				endDate = addDays(10 - numberOfDays, endDate);
			}

			return endDate;
		});

		const days = computed(() =>
			eachDayOfInterval({ start: startOfDay(startDate.value), end: startOfDay(endDate.value) })
				.map(date => ({ date, hasTimeSlots: !!props.availableTimeSlots.getAllForDay(date)?.length }))
		);

		const selectedDay = ref<Date | null>(null);
		const timeSlotsForSelectedDay = computed(() => selectedDay.value ? props.availableTimeSlots.getAllForDay(selectedDay.value) ?? [] : []);
		const selectedTimeSlot = computed({
			get: () => props.modelValue,
			set: value => emit('update:modelValue', value),
		});

		watch(() => [props.availableTimeSlots, props.modelValue] as const, ([availableTimeSlots, selectedTimeSlot]) => {
			nextTick(() => {
				if (selectedTimeSlot) {
					selectedDay.value = days.value.find(({ date }) => isSameDay(date, selectedTimeSlot.start))?.date;
					return;
				} else if (availableTimeSlots) {
					const firstAvailable = availableTimeSlots.values?.[0]?.start;
					if (firstAvailable) {
						selectedDay.value = startOfDay(firstAvailable);
					}
				}
			});
		});

		const timeSlotRowCount = computed(() => Math.ceil((timeSlotsForSelectedDay.value?.length ?? 0) / 2));

		const selectedDayLabel = computed(() => selectedDay.value ? format('EEEE MMMM do, Y', selectedDay.value) : null);

		return {
			selectedDay,
			selectedTimeSlot,
			timeSlotsForSelectedDay,
			days,
			formatTime: format('h:mm a'),
			timeSlotRowCount,
			matchesTimeSlot: (date: Date): boolean => !!selectedTimeSlot.value && isSameDay(selectedTimeSlot.value.start, date),
			selectedDayLabel,
			timeSlotSectionLabel: computed(() => selectedDayLabel.value ? `Available time slots for ${selectedDayLabel.value}` : "Available time slots")
		};
	}
});
